import React from 'react'
import PropTypes from 'prop-types'
import ImageBackground from '../../common/ImageBackground'
import Icon from '../../common/Icon'
import getCoords from './utils'

const GLOBE_TYPES = {
    globeEmpty: 'globeEmpty@2x.png',
    globeWithTrees: 'globeWithTrees@2x.png',
}

const IMAGE_WIDTH = 332
const IMAGE_HEIGHT = 338
const GLOBE_WIDTH = 248
const GLOBE_HEIGHT = 248
const RADIUS = 98
const TREE_HEIGHT = 50
const CORRECTION_X = -45
const CORRECTION_Y = -92
const GLOBE_CORRECTION_X = -80
const GLOBE_CORRECTION_Y = -80
const ANGLES = [10, 233, 92, 120, 300, 69, 178, 270, 38, 100]

const Globe = ({
    imageName,
    trees,
    containerWidth,
    alignSelf,
    style,
}) => {
    // In case the default image size exceeds it's container, then we need to scale
    // down preserving the initial ratio
    const ratio = containerWidth < IMAGE_WIDTH ? containerWidth / IMAGE_WIDTH : 1

    const width = ratio * IMAGE_WIDTH
    const height = ratio * IMAGE_HEIGHT
    const globeWidth = ratio * GLOBE_WIDTH
    const globeHeight = ratio * GLOBE_HEIGHT
    const treeHeight = ratio * TREE_HEIGHT
    const radius = ratio * RADIUS

    const globeCorrection = {
        x: ratio * GLOBE_CORRECTION_X,
        y: ratio * GLOBE_CORRECTION_Y,
    }

    const globeCenter = {
        x: globeHeight / 2 + globeCorrection.x,
        y: globeWidth / 2 + globeCorrection.y,
    }

    const correction = {
        x: ratio * CORRECTION_X,
        y: ratio * CORRECTION_Y,
    }

    const center = {
        x: height / 2 + correction.x,
        y: width / 2 + correction.y,
    }

    // Create an array of numbers such that length = #trees
    const items = [...Array(trees).keys()]

    // Assign a random coord to each tree to be placed
    const coords = []

    items.forEach((item, index) => {
        const { length } = ANGLES
        const cycles = Math.ceil(index / length)
        coords.push(getCoords(center, radius, cycles * 15 + ANGLES[index % length], treeHeight))
    })

    return (
        <ImageBackground
            imageName="movingClouds.gif"
            width={width}
            height={height}
            imageStyle={{
                resizeMode: 'contain',
            }}
            style={{
                alignSelf,
                position: 'relative',
                ...style,
            }}
        >
            <ImageBackground
                imageName={imageName}
                width={globeWidth}
                height={globeHeight}
                imageStyle={{
                    resizeMode: 'contain',
                }}
                style={{
                    alignSelf,
                    position: 'absolute',
                    top: globeCenter.y,
                    left: globeCenter.x,
                }}
            >
                {coords.map(({ x, y, rotate }, index) => (
                    <Icon
                        key={rotate}
                        iconName={index % 3 === 0 ? 'tree2@2x.png' : 'tree@2x.png'}
                        size={treeHeight}
                        rotate={rotate}
                        style={{
                            position: 'absolute',
                            top: y,
                            left: x,
                            height: `${treeHeight}px`,
                        }}
                    />
                ))}
            </ImageBackground>
        </ImageBackground>
    )
}

Globe.propTypes = {
    imageName: PropTypes.oneOf(Object.values(GLOBE_TYPES)).isRequired,
    trees: PropTypes.number,
    containerWidth: PropTypes.number,
    alignSelf: PropTypes.oneOf([
        'center',
        'flex-start',
        'flex-end',
        'stretch',
    ]),
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

Globe.defaultProps = {
    trees: 0,
    containerWidth: IMAGE_WIDTH,
    alignSelf: 'flex-start',
    style: {},
}

export default Globe
